import type { Course } from "@aybuben/core/content/course.ts";
import type { ProgressKey } from "@aybuben/core/content/types.ts";
import "@aybuben/core/exercises/index.ts";
import { exerciseType } from "@aybuben/core/exercises/registry.ts";
import type { LessonStep } from "@aybuben/core/lesson.ts";
import type { Grading, LessonResult, Outcome } from "@aybuben/core/progress/event.ts";
import type { Silencer } from "@aybuben/core/silent.ts";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { HapticKind } from "../haptics.ts";
import { momentAfter, type Answered, type Moment } from "../moments/moments.ts";

/** The longest one gap between two clock reads may add to the active time. */
export const IDLE_CAP_MS = 120_000;

export type LessonSound = "right" | "wrong" | "tick";

/** A wrong answer: the step, what was answered, the key it missed and whether it was a re-ask. */
export interface LessonMiss {
	step: LessonStep;
	answer: unknown;
	key: ProgressKey;
	retry: boolean;
}

/** Graded steps and first-try rights per exercise kind: analytics, not progress. */
export type ExerciseAccuracy = Record<string, { graded: number; first_try: number }>;

/** What `onDone` receives: the progress result, and the per-kind accuracy beside it. */
export type LessonDone = Omit<LessonResult, "startedAt" | "activeMs"> & {
	/** Epoch ms of the first clock read; saved on the event. */
	startedAt: number;
	/** Active ms, saved on the event: the saved copy of `durationMs`, kept equal. */
	activeMs: number;
	exercises: ExerciseAccuracy;
	/** The longest run of first-try rights. Not part of the progress event: callers strip it. */
	bestRun: number;
	/** Active time to the last graded answer, or to the final continue if none. Callers strip it too. */
	durationMs: number;
};

/** What the tap cost the round: the step it was made on and the steps it changed. */
export interface ListeningPause {
	exercise: string;
	stepIndex: number;
	swapped: number;
	dropped: number;
}

export interface LessonRunnerDeps {
	initial: LessonStep[];
	/**
	 * What a wrong pick is read against: a choice's option is a character, and
	 * only the course names the letter. Without it an outcome records no pick.
	 * Not the analytics hook, which an app may skip: the learner's own history
	 * must not depend on analytics being on.
	 */
	course?: Course;
	/** Each transition's cue and the haptic felt with it (null: none); the app plays both. */
	onFeedback?: (cue: LessonSound, haptic: HapticKind | null) => void;
	onDone: (r: LessonDone) => void;
	/** The clock, injectable for tests. Read at creation, at each graded answer and when the app's focus changes. */
	now?: () => number;
	/** Analytics only. */
	onMiss?: (m: LessonMiss) => void;
	/**
	 * Whether the learner had composed a sentence before this lesson. Defaults to
	 * true: a caller that does not know must never claim a first sentence.
	 */
	composedBefore?: boolean;
	/** Absent: the round cannot mute. */
	silence?: Silencer;
	/** The tap, reported before the round it may end is done, so a report of the tap never trails the round's own. */
	onMute?: (p: ListeningPause) => void;
}

export interface LessonRunnerState {
	queue: LessonStep[];
	i: number;
	answer: unknown;
	status: "right" | "wrong" | null;
	done: number;
	mistakes: number;
	firstTry: number;
	/** Consecutive graded steps right on the first try; a wrong check resets it. */
	run: number;
	/** The celebration holding the lesson on the step just answered, until dismissed. */
	moment: Moment | null;
	/** Listening was turned off mid-round: the rest of the queue is silent. */
	muted: boolean;
	/** The steps the round asks for: the initial count less those a mute skipped or dropped. For a progress bar. */
	total: number;
}

export interface LessonRunnerActions {
	setAnswer(a: unknown): void;
	check(): void;
	cont(): void;
	/** `answer` names a miss's pick (a match board's right-hand tile), for `onMiss`. */
	score(key: ProgressKey, ok: boolean, answer?: string): void;
	complete(): void;
	/** The app left or returned to the foreground; time accrues only while it is active. */
	setActive(active: boolean): void;
	/** Close the moment and go to the next step, as `cont` would have. */
	dismissMoment(): void;
	/** Check when unanswered-but-ready, else continue: the Enter key's and action button's one handler. */
	submit(): void;
	/** Whether this step was queued as a re-ask of a missed one (it earns no first-try credit). */
	isRetry(uid: string): boolean;
	/**
	 * Skip the current by-ear step (no outcome, no credit, not a mistake) and
	 * silence everything still queued. Null, and nothing changes, when the round
	 * cannot mute, is already muted, the step is answered or it is not heard.
	 */
	mute(): { swapped: number; dropped: number } | null;
}

export type LessonRunner = StoreApi<LessonRunnerState & LessonRunnerActions>;

/**
 * The step loop of a lesson: a queue of steps, scoring, and a result. It knows
 * nothing about what any step is; every per-kind decision is the registry's.
 */
export function createLessonRunner(d: LessonRunnerDeps): LessonRunner {
	// The ordered record of what the learner did, in the order they did it.
	// The runner owns nothing about scoring, and the order of this list is what
	// lets the reducer reproduce the saturating clamp exactly.
	const outcomes: Outcome[] = [];
	const mark = (k: ProgressKey, right: boolean, via: Grading, picked?: string): void => {
		outcomes.push({ key: k, right, via, ...(picked === undefined ? {} : { picked }) });
	};
	// The item id a wrong pick chose, for types whose miss is one pick among
	// tiles; typed-answer types have no `miss`, so no typed text can land here.
	const pickedBy = (step: LessonStep, answer: unknown): string | undefined => {
		if (!d.course) return undefined;
		try {
			const picked = exerciseType(step.kind).miss?.(step, answer, d.course).picked;
			return typeof picked === "string" ? picked : undefined;
		} catch {
			// An unreadable pick loses only the pick, never the scored outcome.
			return undefined;
		}
	};
	const penalized = new Set<ProgressKey>();
	const retried = new Set<string>();
	// Counted over the questions the round actually asks, like `graded`: a
	// requeued step is not a new question. Muting edits these in place.
	let graded = 0;
	const exercises: ExerciseAccuracy = {};
	const countGraded = (kind: string, by: 1 | -1): void => {
		if (!exerciseType(kind).graded) return;
		graded += by;
		const e = (exercises[kind] ??= { graded: 0, first_try: 0 });
		e.graded += by;
		if (e.graded === 0 && e.first_try === 0) delete exercises[kind];
	};
	for (const s of d.initial) countGraded(s.kind, 1);
	let total = d.initial.length;
	let muteSummary: { swapped: number; dropped: number; skipped: number } | null = null;
	const feedback = d.onFeedback ?? (() => {});
	const now = d.now ?? Date.now;
	const startedAt = now();
	// Active time: the gaps between consecutive clock reads, each capped, counted
	// only while the app is in the foreground. `activeAtGraded` is the total as of
	// the last graded answer, so time spent reading feedback at the end is not billed.
	let active = true;
	let lastRead = startedAt;
	let activeMs = 0;
	let activeAtGraded: number | null = null;
	const accrue = (): number => {
		const t = now();
		if (active) activeMs += Math.min(Math.max(t - lastRead, 0), IDLE_CAP_MS);
		lastRead = t;
		return t;
	};
	const stamp = (): void => {
		accrue();
		activeAtGraded = activeMs;
	};
	let bestRun = 0;
	// Every graded check in order, re-asks included; boards grade through `score`
	// and add none. `answeredAt` is the step index of the last one, so a continue
	// on an ungraded step never reads an older answer as its own.
	const history: Answered[] = [];
	let answeredAt = -1;
	let lastMomentAt: number | null = null;
	let composedBefore = d.composedBefore ?? true;

	return createStore<LessonRunnerState & LessonRunnerActions>()((set, get) => {
		// `i` only ever advances (in `cont`) while `i + 1 < queue.length`, and
		// `queue` only ever grows, so `i` is always a valid index into `queue`.
		const current = (): LessonStep => {
			const { queue, i } = get();
			return queue[i]!;
		};
		const bump = (key: "mistakes" | "firstTry") => set((s) => ({ [key]: s[key] + 1 }));
		// Analytics, reported last and never allowed to throw: a failed report
		// must not leave a miss half-scored.
		const miss = (m: LessonMiss): void => {
			try {
				d.onMiss?.(m);
			} catch {
				/* the miss is scored; only its report is lost */
			}
		};

		const check = () => {
			const { answer, status } = get();
			const step = current();
			const type = exerciseType(step.kind);
			if (answer === null || status || !type.graded) return;
			stamp();
			const ok = type.grade(step, answer);
			history.push({ kind: step.kind, right: ok });
			answeredAt = get().i;
			if (ok) {
				set({ status: "right" });
				type.keys(step).forEach((k) => mark(k, true, "check"));
				if (!retried.has(step.uid)) {
					bump("firstTry");
					// `step.kind` is graded and was in `initial` (a non-retry), so it has an entry.
					exercises[step.kind]!.first_try++;
					const run = get().run + 1;
					bestRun = Math.max(bestRun, run);
					set({ run });
				}
				feedback("right", "correct");
			} else {
				set({ status: "wrong", run: 0 });
				const keys = type.keys(step);
				const retry = retried.has(step.uid);
				const picked = pickedBy(step, answer);
				keys.forEach((k) => mark(k, false, "check", picked));
				bump("mistakes");
				const again = type.requeue?.(step);
				if (again) {
					retried.add(again.uid);
					set((s) => ({ queue: [...s.queue, again] }));
				}
				feedback("wrong", "wrong");
				// Every single-pick type trains one key; the first is the one asked.
				if (keys[0]) miss({ step, answer, key: keys[0], retry });
			}
		};

		const advance = () => set((s) => ({ i: s.i + 1, answer: null, status: null, moment: null }));

		const finish = () => {
			const { mistakes, firstTry } = get();
			// A lesson with no graded answer is timed to its last continue.
			if (activeAtGraded === null) stamp();
			const durationMs = activeAtGraded ?? 0;
			d.onDone({
				outcomes,
				mistakes,
				firstTry,
				graded,
				exercises,
				bestRun,
				durationMs,
				startedAt,
				activeMs: durationMs,
				...(muteSummary ? { muted: muteSummary } : {}),
			});
		};

		const mute = (): { swapped: number; dropped: number } | null => {
			const silence = d.silence;
			const { status, muted, i, queue } = get();
			if (!silence || muted || status !== null) return null;
			const step = queue[i]!; // `i` is always a valid index (see `current`)
			if (!exerciseType(step.kind).heard(step)) return null;
			let swapped = 0;
			let dropped = 0;
			const tail: LessonStep[] = [];
			for (const x of queue.slice(i + 1)) {
				const y = silence(x);
				const reask = retried.has(x.uid);
				if (y === x) {
					tail.push(x);
					continue;
				}
				if (y === null) {
					dropped++;
					// A dropped re-ask takes its missed step's last chance at credit, so its slot goes too.
					total--;
				} else {
					swapped++;
					tail.push(y);
					// A stand-in for a re-ask is still a re-ask: no first-try credit.
					if (reask) retried.add(y.uid);
				}
				if (!reask) {
					countGraded(x.kind, -1);
					if (y) countGraded(y.kind, 1);
				}
			}
			// The skipped step leaves the bar either way, and the graded count unless it was itself a re-ask.
			total--;
			if (!retried.has(step.uid)) countGraded(step.kind, -1);
			muteSummary = { swapped, dropped, skipped: 1 };
			set({ queue: [...queue.slice(0, i + 1), ...tail], muted: true, total });
			d.onMute?.({ exercise: step.kind, stepIndex: i, swapped, dropped });
			// Nothing graded left: the round ends, recorded as a round built muted would be.
			if (!tail.some((x) => exerciseType(x.kind).graded)) finish();
			else advance();
			return { swapped, dropped };
		};

		const cont = () => {
			if (get().moment) return dismissMoment();
			const { status, done, i, queue } = get();
			const credited = status === "right" || !exerciseType(current().kind).graded;
			set({ done: credited ? done + 1 : done });
			if (i + 1 >= queue.length) return finish();
			const moment =
				answeredAt === i
					? momentAfter(history, {
							at: i,
							remaining: queue.length - 1 - i,
							lastAt: lastMomentAt,
							composedBefore,
						})
					: null;
			// Flipped whether or not a moment showed: the gap can suppress the
			// first sentence, and a later right compose is then not a first.
			const last = history[history.length - 1];
			if (answeredAt === i && last?.kind === "compose" && last.right) composedBefore = true;
			if (moment) {
				lastMomentAt = i;
				set({ moment });
				return;
			}
			advance();
		};

		const dismissMoment = () => {
			if (get().moment) advance();
		};

		return {
			queue: d.initial,
			i: 0,
			answer: null,
			status: null,
			done: 0,
			mistakes: 0,
			firstTry: 0,
			run: 0,
			moment: null,
			muted: false,
			total,
			setAnswer: (answer) => set({ answer }),
			check,
			cont,
			score(k, ok, answer) {
				// Reads the clock so a long match board counts as play, not as one idle gap.
				accrue();
				if (ok) {
					mark(k, true, "score");
					// The tile's own press already tapped.
					feedback("tick", null);
				} else {
					bump("mistakes");
					if (!penalized.has(k)) {
						mark(k, false, "score", answer === undefined ? undefined : pickedBy(current(), answer));
						penalized.add(k);
					}
					feedback("wrong", "wrong");
					if (answer !== undefined) miss({ step: current(), answer, key: k, retry: false });
				}
			},
			complete() {
				// A match board is graded by `score`, so it times the round but leaves the run alone.
				stamp();
				set({ status: "right" });
				feedback("right", "correct");
			},
			setActive(next) {
				if (next === active) return;
				// Closing a stretch bills it up to now; opening one restarts the gap from now.
				accrue();
				active = next;
			},
			dismissMoment,
			submit: () => (get().status ? cont() : check()),
			isRetry: (uid) => retried.has(uid),
			mute,
		};
	});
}
